import { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, Zap, Phone } from "lucide-react";
import { SITE } from "@/lib/site";

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/diensten", label: "Diensten" },
  { to: "/over-ons", label: "Over Ons" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/60"
          : "bg-white/40 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2 group">
          <span className="flex items-center justify-center w-9 h-9 rounded-md bg-[#0A1128] text-[#FF6B00] group-hover:bg-[#FF6B00] group-hover:text-white transition-colors">
            <Zap className="w-5 h-5" strokeWidth={2.5} />
          </span>
          <span className="font-display font-black text-lg md:text-xl tracking-tight text-[#0A1128]">
            Care for Elektra
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1" aria-label="Hoofdnavigatie">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                  isActive
                    ? "text-[#FF6B00]"
                    : "text-slate-700 hover:text-[#0A1128]"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:${SITE.phoneIntl}`}
            data-testid="header-call-btn"
            className="flex items-center gap-2 text-sm font-semibold text-[#0A1128] hover:text-[#FF6B00] transition-colors"
          >
            <Phone className="w-4 h-4" />
            {SITE.phoneDisplay}
          </a>
          <Link
            to="/contact"
            data-testid="header-cta-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF6B00] text-white text-sm font-bold hover:bg-[#E66000] transition-colors shadow-sm hover:shadow-lg"
          >
            Offerte aanvragen
          </Link>
        </div>

        <button
          data-testid="header-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-md text-[#0A1128] hover:bg-slate-100"
          aria-label="Menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-slate-200" data-testid="mobile-menu">
          <div className="px-5 py-4 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-md text-base font-semibold ${
                    isActive ? "text-[#FF6B00] bg-orange-50" : "text-slate-800 hover:bg-slate-50"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <a
              href={`tel:${SITE.phoneIntl}`}
              className="mt-2 px-3 py-3 rounded-md text-base font-semibold text-[#0A1128] flex items-center gap-2"
              data-testid="mobile-call-btn"
            >
              <Phone className="w-4 h-4" /> {SITE.phoneDisplay}
            </a>
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="mt-1 inline-flex justify-center px-5 py-3 rounded-full bg-[#FF6B00] text-white font-bold"
              data-testid="mobile-cta-btn"
            >
              Offerte aanvragen
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
